import { useState, useEffect } from 'react';
import { SignedIn, useAuth, useClerk } from '@clerk/clerk-react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';
import QRCodeStyling from 'qr-code-styling';
import { jsPDF } from 'jspdf';
import AddressAutocomplete from '../components/AddressAutocomplete';
import OperatingHoursForm from '../components/OperatingHoursForm';

/**
 * Validates phone: 10+ digits or E.164 (optional + followed by 10+ digits).
 * @param {string} value
 * @returns {boolean}
 */
function isValidPhone(value) {
  const trimmed = (value || '').trim();
  if (!trimmed) return true; // optional field
  // E.164: optional +, then 10–15 digits
  return /^\+?[0-9]{10,15}$/.test(trimmed.replace(/[\s\-\.]/g, ''));
}

export default function Profile() {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [vendorId, setVendorId] = useState(null);
  const [stripeAccountId, setStripeAccountId] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [stripeConnecting, setStripeConnecting] = useState(false);
  const [form, setForm] = useState({
    storeName: '',
    address: '',
    phone: '',
  });
  const [cityName, setCityName] = useState('');
  const [operatingHours, setOperatingHours] = useState([]);
  const [showOperatingHoursForm, setShowOperatingHoursForm] = useState(false);
  const [editingHours, setEditingHours] = useState(null);
  const [phoneError, setPhoneError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api('/api/vendors/me', { getToken });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || `Error ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) {
          setVendorId(data.id);
          setStripeAccountId(data.stripeAccountId);
          setIsOwner(data.isOwner ?? false);
          setForm({
            storeName: data.storeName ?? '',
            address: data.address ?? '',
            phone: data.phone ?? '',
          });
          setCityName(data.city?.name || '');
          
          // Load operating hours
          if (data.id) {
            const hoursRes = await api(`/api/garage-sellers/me/operating-hours`, { getToken });
            if (hoursRes.ok) {
              const hoursData = await hoursRes.json();
              setOperatingHours(hoursData);
            }
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  // Handle Stripe return query params
  useEffect(() => {
    const stripeParam = searchParams.get('stripe');
    if (stripeParam === 'success') {
      setError(null);
      // Refresh vendor data to get updated stripeAccountId
      (async () => {
        try {
          const res = await api('/api/vendors/me', { getToken });
          if (res.ok) {
            const data = await res.json();
            setStripeAccountId(data.stripeAccountId);
          }
        } catch (err) {
          console.error('Failed to refresh vendor data:', err);
        }
      })();
      // Remove query param
      setSearchParams({}, { replace: true });
    } else if (stripeParam === 'pending') {
      setError('Stripe Connect onboarding is still pending. Please complete the process.');
      setSearchParams({}, { replace: true });
    } else if (stripeParam === 'error') {
      const message = searchParams.get('message');
      setError(`Stripe Connect error: ${message || 'Unknown error occurred'}`);
      setSearchParams({}, { replace: true });
    } else if (stripeParam === 'required') {
      setError('Please complete Stripe Connect to access item management features.');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, getToken]);

  const handleStripeConnect = async () => {
    setStripeConnecting(true);
    setError(null);
    try {
      const returnUrl = `${window.location.origin}/profile`;
      const res = await api('/api/vendors/create-account-link', {
        getToken,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          returnUrl,
          refreshUrl: returnUrl,
        }),
      });
      
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || data.message || `Error ${res.status}`);
      }
      
      const data = await res.json();
      if (data.url) {
        // Open Stripe Connect onboarding in new window/tab
        window.location.href = data.url;
      } else {
        throw new Error('No account link URL received');
      }
    } catch (err) {
      setError(err.message);
      setStripeConnecting(false);
    }
  };

  // US Letter 8.5" x 11" at 150 DPI for print
  const FLYER_WIDTH = 1275;
  const FLYER_HEIGHT = 1650;

  const formatDateForFlyer = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleDownloadFlyer = async () => {
    if (!vendorId) {
      setError('Profile not loaded. Please refresh and try again.');
      return;
    }
    setError(null);
    try {
      const vendorUrl = `${window.location.origin}/vendor/${vendorId}`;
      const tempDiv = document.createElement('div');
      tempDiv.style.cssText = 'position:absolute;left:-9999px;width:400px;height:400px;';
      document.body.appendChild(tempDiv);
      const tempQr = new QRCodeStyling({
        width: 400,
        height: 400,
        type: 'canvas',
        data: vendorUrl,
        dotsOptions: { color: '#1e293b', type: 'rounded' },
        backgroundOptions: { color: '#ffffff' },
        cornersSquareOptions: { color: '#1e293b', type: 'extra-rounded' },
        cornersDotOptions: { color: '#1e293b', type: 'dot' },
      });
      tempQr.append(tempDiv);
      await new Promise((r) => setTimeout(r, 150));
      const qrCanvas = tempDiv.querySelector('canvas');
      document.body.removeChild(tempDiv);
      if (!qrCanvas) {
        setError('Could not generate QR code. Try again.');
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = FLYER_WIDTH;
      canvas.height = FLYER_HEIGHT;
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, FLYER_WIDTH, FLYER_HEIGHT);

      const centerX = FLYER_WIDTH / 2;
      let y = 120;

      ctx.fillStyle = '#1e293b';
      ctx.font = 'bold 96px Arial Black, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('GARAGE SALE', centerX, y);
      y += 100;

      if (form.storeName && form.storeName.trim()) {
        ctx.font = 'bold 42px Arial, sans-serif';
        ctx.fillText(form.storeName.trim(), centerX, y);
        y += 60;
      }

      if (form.address && form.address.trim()) {
        ctx.font = '32px Arial, sans-serif';
        const addressLines = form.address.trim().split(/\n|,/).map((s) => s.trim()).filter(Boolean);
        addressLines.forEach((line) => {
          ctx.fillText(line, centerX, y);
          y += 40;
        });
        y += 20;
      }

      if (operatingHours.length > 0) {
        ctx.font = 'bold 28px Arial, sans-serif';
        ctx.fillText('Date & Time', centerX, y);
        y += 44;
        ctx.font = '26px Arial, sans-serif';
        operatingHours.slice(0, 5).forEach((hours) => {
          const dateLabel = hours.saleDate
            ? formatDateForFlyer(hours.saleDate)
            : dayNames[hours.dayOfWeek] ?? 'Day';
          const timeLabel =
            hours.startTime && hours.endTime
              ? `${formatTime(hours.startTime)} – ${formatTime(hours.endTime)}`
              : hours.startTime
                ? `Starts ${formatTime(hours.startTime)}`
                : 'Time TBD';
          ctx.fillText(`${dateLabel}  •  ${timeLabel}`, centerX, y);
          y += 38;
        });
        y += 30;
      }

      const qrSize = 320;
      const qrX = centerX - qrSize / 2;
      ctx.drawImage(qrCanvas, qrX, y, qrSize, qrSize);
      y += qrSize + 36;

      ctx.font = 'bold 28px Arial, sans-serif';
      ctx.fillStyle = '#4338ca';
      ctx.fillText('See what\'s in my garage sale!', centerX, y);
      y += 40;
      ctx.font = '24px Arial, sans-serif';
      ctx.fillStyle = '#1e293b';
      ctx.fillText('Scan to view awesome deals.', centerX, y);

      // Export as PDF (US Letter 8.5" x 11" — canvas is already letter proportions)
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'in', format: 'letter' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pdfW, pdfH);
      pdf.save(`garage-sale-flyer-${vendorId}.pdf`);

      try {
        await api('/api/vendors/me', {
          getToken,
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ profileQrUrl: vendorUrl }),
        });
      } catch (_) {}
    } catch (err) {
      console.error('Error generating flyer:', err);
      setError(err?.message || 'Could not generate flyer. Try again.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'phone') setPhoneError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const phoneTrimmed = form.phone.trim();
    if (phoneTrimmed && !isValidPhone(form.phone)) {
      setPhoneError('Enter 10+ digits or E.164 (e.g. +1234567890)');
      return;
    }
    setPhoneError(null);
    setSaving(true);
    setError(null);
    try {
      const res = await api('/api/vendors/me', {
        getToken,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeName: form.storeName || null,
          address: form.address || null,
          phone: phoneTrimmed || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || data.error || `Error ${res.status}`);
      }
      // Refresh data to get updated city name
      const refreshRes = await api('/api/vendors/me', { getToken });
      if (refreshRes.ok) {
        const refreshData = await refreshRes.json();
        setCityName(refreshData.city?.name || '');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddOperatingHours = async (hoursData) => {
    try {
      // Convert time strings to format backend expects
      const payload = {
        saleDate: hoursData.saleDate,
        startTime: hoursData.startTime && hoursData.startTime.trim() ? hoursData.startTime.trim() : null,
        endTime: hoursData.endTime && hoursData.endTime.trim() ? hoursData.endTime.trim() : null
      };
      console.log('Sending payload to backend:', payload);
      const res = await api('/api/garage-sellers/me/operating-hours', {
        getToken,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Error ${res.status}`);
      }
      // Reload operating hours
      const hoursRes = await api('/api/garage-sellers/me/operating-hours', { getToken });
      if (hoursRes.ok) {
        const hoursData = await hoursRes.json();
        setOperatingHours(hoursData);
      }
      setShowOperatingHoursForm(false);
      setEditingHours(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateOperatingHours = async (id, hoursData) => {
    try {
      // Convert time strings to format backend expects
      const payload = {
        saleDate: hoursData.saleDate,
        startTime: hoursData.startTime && hoursData.startTime.trim() ? hoursData.startTime.trim() : null,
        endTime: hoursData.endTime && hoursData.endTime.trim() ? hoursData.endTime.trim() : null
      };
      console.log('Sending update payload to backend:', payload);
      const res = await api(`/api/garage-sellers/me/operating-hours/${id}`, {
        getToken,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Error ${res.status}`);
      }
      // Reload operating hours
      const hoursRes = await api('/api/garage-sellers/me/operating-hours', { getToken });
      if (hoursRes.ok) {
        const hoursData = await hoursRes.json();
        setOperatingHours(hoursData);
      }
      setShowOperatingHoursForm(false);
      setEditingHours(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteOperatingHours = async (id) => {
    if (!window.confirm('Are you sure you want to delete this operating hour?')) return;
    try {
      const res = await api(`/api/garage-sellers/me/operating-hours/${id}`, {
        getToken,
        method: 'DELETE',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Error ${res.status}`);
      }
      // Reload operating hours
      const hoursRes = await api('/api/garage-sellers/me/operating-hours', { getToken });
      if (hoursRes.ok) {
        const hoursData = await hoursRes.json();
        setOperatingHours(hoursData);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-2xl text-slate-500">Loading profile…</p>
      </div>
    );
  }

  const hasStripeAccount = stripeAccountId != null && stripeAccountId.trim() !== '';

  return (
    <SignedIn>
      <div className="flex flex-col gap-6">
        <header className="flex items-center justify-between gap-4">
          <h1 className="text-3xl font-semibold">Garage Sale Profile</h1>
          <button
            type="button"
            onClick={() => signOut({ redirectUrl: '/' })}
            className="shrink-0 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 underline underline-offset-2"
          >
            Sign out
          </button>
        </header>

        {/* City Assignment Info */}
        {cityName && (
          <div className="rounded-xl border-2 border-indigo-400 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 p-4">
            <p className="text-lg text-indigo-900 dark:text-indigo-100">
              <span className="font-semibold">City:</span> {cityName}
            </p>
            <p className="text-sm text-indigo-700 dark:text-indigo-300 mt-1">
              Your garage sale is listed in {cityName}. Changing your address will update your city assignment.
            </p>
          </div>
        )}

        {/* Stripe Connect Section */}
        {!hasStripeAccount && (
          <div className="rounded-xl border-2 border-amber-400 dark:border-amber-500 bg-amber-50 dark:bg-amber-900/20 p-6 flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <h2 className="text-2xl font-semibold text-amber-900 dark:text-amber-100">
                Complete Stripe Connect
              </h2>
              <p className="text-lg text-amber-800 dark:text-amber-200">
                You need to complete Stripe Connect onboarding to list items and manage your store. This allows you to receive payments securely.
              </p>
            </div>
            <button
              type="button"
              onClick={handleStripeConnect}
              disabled={stripeConnecting}
              className="h-16 text-2xl w-full rounded-xl font-semibold bg-amber-600 text-white hover:bg-amber-700 active:bg-amber-800 disabled:opacity-50 transition-colors"
            >
              {stripeConnecting ? 'Connecting…' : 'Complete Stripe Connect'}
            </button>
          </div>
        )}

        {error && (
          <p className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
            {error}
          </p>
        )}
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <label className="flex flex-col gap-2">
            <span className="text-lg font-medium">{isOwner ? 'Fleamarket Name' : 'Garage Sale Name'}</span>
            <input
              type="text"
              name="storeName"
              value={form.storeName}
              onChange={handleChange}
              className="h-16 text-2xl w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4"
              placeholder={isOwner ? 'Fleamarket Name' : 'Garage Sale Name'}
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-lg font-medium">Address</span>
            <AddressAutocomplete
              value={form.address}
              onChange={(value) => setForm(prev => ({ ...prev, address: value }))}
              placeholder="Enter your garage sale address"
              disabled={saving}
              className="h-16 text-xl w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4"
            />
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Changing your address will automatically update your city assignment.
            </p>
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-lg font-medium">Phone</span>
            <input
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              className="h-16 text-2xl w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4"
              placeholder="+1 234 567 8900"
            />
            {phoneError && (
              <p className="text-red-600 dark:text-red-400 text-base">{phoneError}</p>
            )}
          </label>
          <button
            type="submit"
            disabled={saving}
            className="h-16 text-2xl w-full rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : 'Save profile'}
          </button>
        </form>

        {/* Operating Hours Section */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold">Operating Hours</h2>
            <button
              type="button"
              onClick={() => {
                setEditingHours(null);
                setShowOperatingHoursForm(!showOperatingHoursForm);
              }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              {showOperatingHoursForm ? 'Cancel' : 'Add Day'}
            </button>
          </div>

          {showOperatingHoursForm && (
            <OperatingHoursForm
              onSubmit={(data) => {
                if (editingHours) {
                  handleUpdateOperatingHours(editingHours.id, data);
                } else {
                  handleAddOperatingHours(data);
                }
              }}
              onCancel={() => {
                setShowOperatingHoursForm(false);
                setEditingHours(null);
              }}
              initialData={editingHours}
            />
          )}

          {operatingHours.length === 0 ? (
            <p className="text-slate-500 dark:text-slate-400 text-center py-4">
              No operating hours added yet. Click "Add Day" to add your garage sale schedule.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {operatingHours.map((hours) => {
                const formatDate = (dateStr) => {
                  if (!dateStr) return '';
                  const date = new Date(dateStr);
                  return date.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  });
                };
                return (
                  <div
                    key={hours.id}
                    className="flex items-center justify-between p-4 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-800"
                  >
                    <div className="flex flex-col gap-1">
                      <span className="font-semibold">
                        {hours.saleDate ? formatDate(hours.saleDate) : (hours.dayOfWeek !== null && hours.dayOfWeek !== undefined ? dayNames[hours.dayOfWeek] : 'Date')}
                      </span>
                      {hours.startTime && hours.endTime ? (
                        <span className="text-slate-600 dark:text-slate-400">
                          {formatTime(hours.startTime)} - {formatTime(hours.endTime)}
                        </span>
                      ) : hours.startTime ? (
                        <span className="text-slate-600 dark:text-slate-400">
                          Starts at {formatTime(hours.startTime)}
                        </span>
                      ) : (
                        <span className="text-slate-500 dark:text-slate-500 text-sm">No time specified</span>
                      )}
                    </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingHours(hours);
                        setShowOperatingHoursForm(true);
                      }}
                      className="text-indigo-600 dark:text-indigo-400 hover:underline text-sm"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteOperatingHours(hours.id)}
                      className="text-red-600 dark:text-red-400 hover:underline text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {vendorId && (
          <div className="flex flex-col gap-4 items-center">
            <h2 className="text-2xl font-semibold">Your Flyer</h2>
            <p className="text-slate-600 dark:text-slate-400 text-center max-w-md">
              Download a PDF flyer (8.5″ × 11″) for printing. Includes garage sale title, address, date & time, and a QR code so people can scan to see your items.
            </p>
            <button
              type="button"
              onClick={handleDownloadFlyer}
              className="h-16 text-2xl w-full rounded-xl font-semibold bg-slate-600 text-white hover:bg-slate-700 active:bg-slate-800 transition-colors"
            >
              Download Your Flyer
            </button>
          </div>
        )}

      </div>
    </SignedIn>
  );
}
